/**
 * Protected Route Component
 * Redirects to login if user is not authenticated or session expired
 */

import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSchedulerAuth } from '../../contexts/SchedulerAuthContext'
import { isSessionExpired } from '../../utils/scheduler/auth'

export function ProtectedRoute({ children, requireAdmin = false, deniedRedirectTo = null }) {
  // Safely get auth context - handle case where context might not be available during error recovery
  // This can happen during React's error boundary recovery or hot module reload
  let authContext
  try {
    authContext = useSchedulerAuth()
  } catch (error) {
    // If context is not available (e.g., during error recovery), show loading state
    // This prevents the app from crashing and allows React to recover
    if (process.env.NODE_ENV === 'development') {
      console.warn('ProtectedRoute: SchedulerAuthContext not available - this may happen during error recovery', error)
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  const { user, loading, isAdmin, canAccessScheduler } = authContext

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  // Check if session is expired (even if user exists in state)
  if (user && isSessionExpired()) {
    return <Navigate to="/hub/login" replace />
  }

  if (!user) {
    return <Navigate to="/hub/login" replace />
  }

  if (!canAccessScheduler) {
    return <Navigate to="/hub" replace />
  }

  if (requireAdmin && !isAdmin) {
    if (deniedRedirectTo) {
      return <Navigate to={deniedRedirectTo} replace />
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return children
}
